import { useState, useEffect } from "react";
import { useForm } from "../../../hooks/useForm";
import OmniTable from "../../omni-common/components/OmniTable";
import OriginalPostSearchFilters from "../components/OriginalPostSearchFilters";
import OriginalPostTable, { OriginalPost } from "../components/OriginalPostTable";
import OriginalPostBulkActions from "../components/OriginalPostBulkActions";
import AddOriginalPostModal from "../components/AddOriginalPostModal";
import OriginalPostDetailModal from "../components/OriginalPostDetailModal";
import { useOmniOriginalPostsQuery, OriginalPostSummary, useOmniSelectOriginalPostMutation, useOmniDeleteOriginalPostMutation, useOmniCreateOriginalPostMutation, useOmniRequestDraftCreationMutation, SearchFieldType, OriginalPostStatus, SortOrder } from "../../../lib/apollo/graphql/omni/omni-generated";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useAdminUserContext } from "../../common/providers/AdminUserProvider";

interface DateRange {
  startDate?: string;
  endDate?: string;
}

export default function OmniOriginalPostContainer() {
  const [filters, onChange] = useForm({
    page: 1,
    limit: 10,
    searchQuery: "",
    searchType: "ALL",
    statusFilter: "",
    dateTypeFilter: "CREATED",
    sortKey: "",
    sortDirection: "ASC" as "ASC" | "DESC",
  });

  const [dateRange, setDateRange] = useState<DateRange>({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedDatePreset, setSelectedDatePreset] = useState<string>("ALL");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<OriginalPost | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // 검색 필드 설정 함수
  const getSearchFields = (searchType: string): SearchFieldType[] | undefined => {
    switch (searchType) {
      case "TITLE":
        return [SearchFieldType.Title];
      case "CONTENT_TAG":
        // 내용+태그는 내용으로 검색하고 태그는 별도 필터 사용
        return [SearchFieldType.Content];
      case "TAG":
        // TAG인 경우 tags 필터로 처리하므로 undefined 반환
        return undefined;
      case "ALL":
        // ALL인 경우 모든 필드 검색
        return [SearchFieldType.Title, SearchFieldType.Content];
      default:
        return undefined;
    }
  };

  // GraphQL 쿼리 훅 - 필터와 검색 조건 적용
  const isTagSearch = filters.searchType === "TAG" && filters.searchQuery?.trim();
  const isContentTagSearch = filters.searchType === "CONTENT_TAG" && filters.searchQuery?.trim();
  const isAllSearch = filters.searchType === "ALL" && filters.searchQuery?.trim();
  const isTextSearch = filters.searchQuery?.trim() && filters.searchType !== "ALL" && filters.searchType !== "TAG" && filters.searchType !== "CONTENT_TAG";

  // 한국 시간 기준 날짜를 UTC로 변환하는 함수
  const convertDateRangeToUTC = (range: DateRange) => {
    if (!range.startDate && !range.endDate) return undefined;

    const result: { startDate?: string; endDate?: string } = {};

    if (range.startDate) {
      // 선택한 날짜의 전날 15:00:00.000Z (한국 시간 당일 00:00:00)
      const [year, month, day] = range.startDate.split('-');
      const selectedDate = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day), 0, 0, 0, 0));
      const prevDay = new Date(selectedDate.getTime() - (24 * 60 * 60 * 1000));
      result.startDate = `${prevDay.getUTCFullYear()}-${String(prevDay.getUTCMonth() + 1).padStart(2, '0')}-${String(prevDay.getUTCDate()).padStart(2, '0')}T15:00:00.000Z`;
    }

    if (range.endDate) {
      // 선택한 날짜 14:59:59.999Z (한국 시간 당일 23:59:59.999)
      result.endDate = `${range.endDate}T14:59:59.999Z`;
    }

    return result;
  };

  const queryVariables = {
    filter: {
      pagination: {
        page: filters.page,
        limit: filters.limit,
      },
      sortBy: filters.sortKey || undefined,
      sortOrder: filters.sortDirection ? (filters.sortDirection === "ASC" ? SortOrder.Asc : SortOrder.Desc) : undefined,
      status: filters.statusFilter ? (filters.statusFilter as OriginalPostStatus) : undefined,
      search: (isTextSearch || isContentTagSearch || isAllSearch) ? {
        fields: getSearchFields(filters.searchType),
        keyword: filters.searchQuery.trim(),
      } : undefined,
      dateRange: convertDateRangeToUTC(dateRange),
      tags: (isTagSearch || isContentTagSearch || isAllSearch) ? [filters.searchQuery.trim()] : undefined,
    }
  };

  const { data, loading, error, refetch } = useOmniOriginalPostsQuery({
    variables: queryVariables
  });
  
  // selectOriginalPost mutation 훅
  const [selectOriginalPost, { loading: selectLoading }] = useOmniSelectOriginalPostMutation();
  const [deleteOriginalPost, { loading: deleteLoading }] = useOmniDeleteOriginalPostMutation();
  const [createOriginalPost, { loading: createLoading }] = useOmniCreateOriginalPostMutation();
  const [requestDraftCreation, { loading: draftCreationLoading }] = useOmniRequestDraftCreationMutation();

  // API 데이터를 로컬 형식으로 변환
  const convertApiDataToLocal = (apiPosts: OriginalPostSummary[]): OriginalPost[] => {
    return apiPosts.map(post => ({
      id: post.originalPostId,
      title: post.title,
      status: post.status,
      tags: post.tags,
      createdAt: post.createdAt,
      updatedAt: post.updatedAt,
      sourceUrl: post.sourceUrl,
      content: "",
      memo: "",
      draft: undefined, // API에서 draft 필드를 아직 지원하지 않으므로 임시로 undefined 설정
    }));
  };

  // 에러 처리
  useEffect(() => {
    if (error) {
      console.error('Failed to fetch original posts:', error);
    }
  }, [error]);

  // 데이터가 업데이트되면 선택 해제
  useEffect(() => {
    setSelectedIds([]);
  }, [data]);

  // API 사용 시에는 서버에서 필터링된 데이터가 오므로 로컬 필터링 대신 API 데이터 사용
  const posts = data?.originalPosts?.items ? convertApiDataToLocal(data.originalPosts.items) : [];
  const totalPages = data?.originalPosts?.totalPages || 0;
  const total = data?.originalPosts?.total || 0;

  const handleSearch = () => {
    // 검색 시 API 다시 호출 (자동으로 필터 적용됨)
    refetch();
  };

  const handleDatePresetSelect = (preset: string) => {
    setSelectedDatePreset(preset);
    // 한국 시간(KST, UTC+9) 기준으로 오늘 날짜 가져오기
    const getKSTDate = () => {
      const now = new Date();
      // UTC 시간에 9시간을 더해 한국 시간으로 변환
      const kstOffset = 9 * 60; // 9시간을 분으로 변환
      const utc = now.getTime() + (now.getTimezoneOffset() * 60000); // UTC 시간
      return new Date(utc + (kstOffset * 60000)); // KST 시간
    };
    const today = getKSTDate();
    // 날짜를 YYYY-MM-DD 형식으로 변환 (화면 표시용)
    const getDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    switch (preset) {
      case "ALL":
        setDateRange({});
        break;
      case "TODAY":
        setDateRange({ startDate: getDateString(today), endDate: getDateString(today) });
        break;
      case "3DAYS":
        const threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(today.getDate() - 3);
        setDateRange({ startDate: getDateString(threeDaysAgo), endDate: getDateString(today) });
        break;
      case "7DAYS":
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        setDateRange({ startDate: getDateString(sevenDaysAgo), endDate: getDateString(today) });
        break;
      case "1MONTH":
        const oneMonthAgo = new Date(today);
        oneMonthAgo.setMonth(today.getMonth() - 1);
        setDateRange({ startDate: getDateString(oneMonthAgo), endDate: getDateString(today) });
        break;
      case "6MONTHS":
        const sixMonthsAgo = new Date(today);
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        setDateRange({ startDate: getDateString(sixMonthsAgo), endDate: getDateString(today) });
        break;
      case "1YEAR":
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);
        setDateRange({ startDate: getDateString(oneYearAgo), endDate: getDateString(today) });
        break;
      case "CUSTOM":
        break;
    }
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      setSelectedIds(posts.map(post => post.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectPost = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedIds(prev => [...prev, id]);
    } else {
      setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
    }
  };

  const handleSchedulePublish = async () => {
    if (selectedIds.length === 0) {
      alert('발행예정 처리할 원문을 선택해주세요.');
      return;
    }
    
    if (!window.confirm(`선택한 ${selectedIds.length}개의 원문을 발행예정 상태로 변경하시겠습니까?`)) {
      return;
    }
    
    try {
      // 선택된 원문들을 발행예정 상태로 변경
      const promises = selectedIds.map(originalPostId =>
        selectOriginalPost({
          variables: {
            input: {
              originalPostId: originalPostId,
            }
          }
        })
      );
      
      const results = await Promise.all(promises);
      
      
      // 실패한 요청 확인
      const failedRequests = results.filter(result => !result.data?.selectOriginalPost);
      
      if (failedRequests.length > 0) {
        const successCount = selectedIds.length - failedRequests.length;
        alert(`${successCount}개 성공, ${failedRequests.length}개 발행예정 처리에 실패했습니다.`);
      } else {
        alert(`${selectedIds.length}개의 원문이 발행예정 상태로 변경되었습니다.`);
      }
      
      // 선택 해제 및 데이터 새로고침
      setSelectedIds([]);
      await refetch();
      
      // 상세 모달이 열리고 있는 post가 변경된 경우 selectedPost도 업데이트
      if (selectedPost && selectedIds.includes(selectedPost.id)) {
        setSelectedPost(null);
        setIsDetailModalOpen(false);
      }
      
    } catch (error) {
      console.error('Failed to select original posts:', error);
      alert('발행예정 처리에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      alert("삭제할 항목을 선택해주세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedIds.length}개의 원문을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }

    try {
      // 병렬로 삭제 요청 실행
      const promises = selectedIds.map(originalPostId =>
        deleteOriginalPost({
          variables: {
            input: {
              originalPostId,
            }
          }
        })
      );

      const results = await Promise.all(promises);
      
      // 모든 삭제가 성공했는지 확인 (문자열 반환값이 있으면 성공)
      const failedDeletes = results.filter(result => !result.data?.deleteOriginalPost);
      
      if (failedDeletes.length > 0) {
        alert(`${failedDeletes.length}개의 삭제가 실패했습니다. 다시 시도해주세요.`);
      } else {
        alert(`${selectedIds.length}개의 원문이 성공적으로 삭제되었습니다.`);
      }
      
      // 선택 초기화 및 데이터 새로고침
      setSelectedIds([]);
      await refetch(); // refetch 완료 대기
    } catch (error) {
      console.error('Failed to delete original posts:', error);
      alert('원문 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handlePostClick = (post: OriginalPost) => {
    setSelectedPost(post);
    setIsDetailModalOpen(true);
  };

  const handleSort = (key: string, direction: "ASC" | "DESC") => {
    onChange("sortKey", key);
    onChange("sortDirection", direction);
  };

  // 초기화 기능
  const handleReset = () => {
    // 모든 필터 초기화
    onChange("searchQuery", "");
    onChange("searchType", "ALL");
    onChange("statusFilter", "");
    onChange("dateTypeFilter", "CREATED");
    onChange("sortKey", "");
    onChange("sortDirection", "ASC");
    onChange("page", 1);
    
    // 날짜 관련 초기화
    setDateRange({});
    setSelectedDatePreset("ALL");
    
    // 선택된 항목 초기화
    setSelectedIds([]);
  };

  // 원문 추가 기능
  const handleAddOriginalPost = () => {
    setIsAddModalOpen(true);
  };

  const handleSaveOriginalPost = async (postData: Omit<OriginalPost, "id" | "createdAt" | "updatedAt">) => {
    try {
      const createInput = {
        title: postData.title,
        content: postData.content || "",
        sourceUrl: postData.sourceUrl || "",
        tags: postData.tags.length > 0 ? postData.tags : undefined,
        memo: postData.memo || undefined,
        // publishedAt는 일단 생략 (서버에서 자동 설정)
      };

      const result = await createOriginalPost({
        variables: { input: createInput }
      });

      if (result.data?.createOriginalPost) {
        alert('원문이 성공적으로 추가되었습니다.');
        setIsAddModalOpen(false);
        await refetch(); // 데이터 새로고침 완료 대기
      } else {
        throw new Error('Create operation failed');
      }
    } catch (error: any) {
      console.error('Failed to create original post:', error);

      // GraphQL 에러 메시지 확인
      const errorMessage = error?.graphQLErrors?.[0]?.message || error?.message || '';

      if (errorMessage.includes('already exists')) {
        alert('해당 출처의 원문이 이미 존재합니다.');
      } else {
        alert('원문 추가에 실패했습니다. 다시 시도해주세요.');
      }

      // 에러를 다시 throw하여 모달이 닫히지 않도록 함
      throw error;
    }
  };

  // 상세 모달에서 저장/수정/삭제 처리
  const handleDetailSave = (updatedPost: OriginalPost) => {
    // TODO: API 호출로 게시물 수정
    setIsDetailModalOpen(false);
    setSelectedPost(null);
    refetch(); // 데이터 새로고침
  };

  const handleDetailDelete = async (postId: string) => {
    try {
      const result = await deleteOriginalPost({
        variables: {
          input: {
            originalPostId: postId,
          }
        }
      });

      if (result.data?.deleteOriginalPost) {
        alert('원문이 성공적으로 삭제되었습니다.');
        setIsDetailModalOpen(false);
        setSelectedPost(null);
        await refetch(); // 데이터 새로고침 완료 대기
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      console.error('Failed to delete original post:', error);
      alert('원문 삭제에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleSchedulePublishSingle = async (postId: string) => {

    // 발행중 상태의 다른 아이템이 있는지 확인 (현재 아이템 제외)
    const hasDraftRequested = data?.originalPosts?.items?.some(
      post => post.status === "DRAFT_REQUESTED" && post.originalPostId !== postId
    );

    if (hasDraftRequested) {
      alert('현재 발행중인 다른 원문이 있습니다. 발행이 완료된 후 다시 시도해주세요.');
      return;
    }

    if (!window.confirm('이 원문을 발행하시겠습니까?')) {
      return;
    }

    try {
      const result = await requestDraftCreation({
        variables: {
          input: {
            originalPostId: postId,
          }
        }
      });

      if (result.data?.requestDraftCreation) {
        alert('원고 발행 요청이 성공했습니다.');
        setIsDetailModalOpen(false);
        setSelectedPost(null);
        await refetch();
      } else {
        
        // GraphQL 에러가 있는 경우 구체적인 에러 메시지 표시
        if (result.errors && result.errors.length > 0) {
          const errorMessage = result.errors[0].message;
          if (errorMessage.includes('Cannot request draft generation from status')) {
            alert('이미 발행 중이거나 발행 완료된 원문입니다.');
          } else {
            alert(`발행 요청 실패: ${errorMessage}`);
          }
        } else {
          alert('원고 발행 요청에 실패했습니다. 다시 시도해주세요.');
        }
        return;
      }
    } catch (error) {
      console.error('Failed to request draft creation:', error);
      alert('원고 발행 요청에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="w-full min-w-[1200px] max-w-full bg-zinc-100 p-3 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h1 className="text-2xl font-bold">원문관리</h1>
        <button
          onClick={handleRefresh}
          disabled={isRefreshing || loading}
          className="flex items-center justify-center p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
          title="새로고침"
        >
          <ArrowPathIcon
            className={`h-5 w-5 transition-transform duration-500 ${
              isRefreshing ? 'animate-spin' : ''
            }`}
          />
        </button>
      </div>

      <div className="mb-4 flex-shrink-0">
        <OriginalPostSearchFilters
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => onChange("searchQuery", value)}
          onSearch={handleSearch}
          searchType={filters.searchType}
          onSearchTypeChange={(value) => onChange("searchType", value)}
          statusFilter={filters.statusFilter}
          onStatusChange={(value) => onChange("statusFilter", value)}
          dateTypeFilter={filters.dateTypeFilter}
          onDateTypeChange={(value) => onChange("dateTypeFilter", value)}
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          onDatePresetSelect={handleDatePresetSelect}
          selectedDatePreset={selectedDatePreset}
          onReset={handleReset}
          onAddOriginalPost={handleAddOriginalPost}
        />
      </div>

      <div className="flex-1 min-h-0 mb-4">
        <OriginalPostTable
          posts={posts}
          selectedIds={selectedIds}
          onSelectAll={handleSelectAll}
          onSelectPost={handleSelectPost}
          onPostClick={handlePostClick}
          onPublishSingle={handleSchedulePublishSingle}
          isLoading={loading}
          sortKey={filters.sortKey}
          sortDirection={filters.sortDirection}
          onSort={handleSort}
        />
      </div>

      <div className="flex flex-col gap-3 flex-shrink-0" style={{ minHeight: selectedIds.length > 0 ? '140px' : '80px' }}>
        <OmniTable.Pagination
          total={totalPages}
          page={filters.page}
          onPageChange={(page) => onChange("page", page)}
        />

        {total > 0 && (
          <div className="flex justify-center text-sm text-gray-600">
            총 {total.toLocaleString()}개의 원문
          </div>
        )}

        <OriginalPostBulkActions
          selectedCount={selectedIds.length}
          onSchedulePublish={handleSchedulePublish}
          onDelete={handleDelete}
          isLoading={selectLoading || deleteLoading || draftCreationLoading}
        />
      </div>

      <AddOriginalPostModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleSaveOriginalPost}
        isLoading={createLoading}
      />

      <OriginalPostDetailModal
        key={selectedPost?.id} // post가 변경될 때마다 컴포넌트 재마운트
        post={selectedPost}
        open={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedPost(null);
        }}
        onSave={handleDetailSave}
        onDelete={handleDetailDelete}
        onSchedulePublish={handleSchedulePublishSingle}
      />
    </div>
  );
}
